// FILE: src/pages/admin/AdminAccommodationsPage.jsx
// Accommodation requests sent before registration (MTO 12.3; AODA). Staff
// see each request, change its status and keep notes; every change is
// audited on the server. Tabs (New / In progress / Closed / All, each with
// its count) filter on the server and are kept in the URL (?status=&page=).
// Each request links to its own page (/admin/accommodations/:id), which is
// where the staff notification email points.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, LinearProgress, Pagination, Stack, Typography } from "@mui/material";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../../components/ui";
import { FilterTabs } from "../../components/admin/AdminBits";
import AccommodationRequestCard, { ACCOMMODATION_STATUS } from "../../components/admin/AccommodationRequestCard";

const LIMIT = 25;
const VIEWS = [
  { value: "NEW", label: "New" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "CLOSED", label: "Closed" },
  { value: "all", label: "All" }
];

const EMPTY_COPY = {
  NEW: { title: "No new requests", description: "New requests from the public Request an accommodation page appear here." },
  IN_PROGRESS: {
    title: "Nothing in progress",
    description: "Requests you're working on appear here once you set them to In progress."
  },
  CLOSED: { title: "No closed requests yet", description: "Requests you close appear here." },
  all: { title: "No requests yet", description: "Requests from the public Request an accommodation page will appear here." }
};

export default function AdminAccommodationsPage() {
  const [params, setParams] = useSearchParams();
  const view = VIEWS.some((v) => v.value === params.get("status")) ? params.get("status") : "NEW";
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);

  const [state, setState] = useState({ items: null, totalCount: 0, loading: true, error: null });
  const [counts, setCounts] = useState({});
  const [notice, setNotice] = useState("");
  const latest = useRef(0);

  const loadCounts = useCallback(() => {
    VIEWS.forEach(({ value }) => {
      const q = value === "all" ? "limit=1" : `status=${value}&limit=1`;
      fetchWithAuth(`/api/admin/accommodations?${q}`)
        .then((data) => setCounts((c) => ({ ...c, [value]: Number(data?.totalCount) || 0 })))
        .catch(() => setCounts((c) => ({ ...c, [value]: undefined })));
    });
  }, []);

  const load = useCallback(() => {
    const id = ++latest.current;
    const q = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
    if (view !== "all") q.set("status", view);
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth(`/api/admin/accommodations?${q.toString()}`)
      .then((data) => {
        if (id !== latest.current) return;
        const items = Array.isArray(data?.requests) ? data.requests : Array.isArray(data) ? data : [];
        setState({ items, totalCount: Number(data?.totalCount) || items.length, loading: false, error: null });
      })
      .catch((error) => {
        if (id === latest.current) setState((s) => ({ ...s, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [view, page]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const refresh = () => {
    load();
    loadCounts();
  };

  const saved = (updated) => {
    setNotice(updated?.name ? `The request from ${updated.name} was updated.` : "The request was updated.");
    refresh();
  };

  const setView = (value) => {
    const next = new URLSearchParams(params);
    if (value === "NEW") next.delete("status");
    else next.set("status", value);
    next.delete("page");
    setNotice("");
    setParams(next, { replace: true });
  };

  const setPage = (value) => {
    const next = new URLSearchParams(params);
    if (value <= 1) next.delete("page");
    else next.set("page", String(value));
    setParams(next);
  };

  const { items, totalCount, loading, error } = state;
  const pages = Math.max(1, Math.ceil(totalCount / LIMIT));
  const viewLabel = VIEWS.find((v) => v.value === view).label;

  return (
    <Box>
      <PageHeader
        title="Accommodation requests"
        description="Requests sent from the public site before registration. Reply by the person's preferred contact, then close the request."
      />

      {notice && (
        <Alert severity="success" role="status" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <FilterTabs
        label="Request status"
        idPrefix="accommodations"
        value={view}
        onChange={setView}
        options={VIEWS.map((v) => {
          const Icon = ACCOMMODATION_STATUS[v.value]?.Icon;
          return { ...v, count: counts[v.value], icon: Icon ? <Icon aria-hidden /> : undefined };
        })}
      />

      <Box id="accommodations-panel" role="tabpanel" aria-labelledby={`accommodations-tab-${view}`}>
        {items === null && error ? (
          <ErrorState error={error} title="We couldn't load the requests" onRetry={refresh} />
        ) : items === null ? (
          <LoadingState variant="table" rows={3} label="Loading requests…" />
        ) : (
          <>
            {error && <ErrorState compact error={error} onRetry={refresh} />}
            <Box sx={{ height: 8, mb: 1 }}>{loading && <LinearProgress aria-label="Updating the requests" />}</Box>
            {items.length === 0 ? (
              <EmptyState {...EMPTY_COPY[view]} />
            ) : (
              <>
                <Typography role="status" sx={{ color: "text.secondary", mb: 1.5 }}>
                  {`${totalCount} ${totalCount === 1 ? "request" : "requests"}${view === "all" ? "" : ` · ${viewLabel}`}, newest first`}
                </Typography>
                <Stack spacing={2}>
                  {items.map((item) => (
                    <AccommodationRequestCard key={item._id} item={item} onSaved={saved} />
                  ))}
                </Stack>
                {pages > 1 && (
                  <Pagination
                    count={pages}
                    page={Math.min(page, pages)}
                    onChange={(_, p) => setPage(p)}
                    getItemAriaLabel={(type, p, selected) =>
                      type === "page" ? `${selected ? "Current page, " : "Go to "}page ${p}` : `Go to the ${type} page`
                    }
                    sx={{ mt: 3, display: "flex", justifyContent: "center" }}
                  />
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
